import React, { useEffect, useRef, useState } from 'react';
import './SelectorColumns.css';
import { FaColumns } from 'react-icons/fa';
import { MdDragIndicator } from 'react-icons/md';
import { RxReset } from "react-icons/rx";
import Button from '../../common/Button';
import { rawColumnsByMode } from '../../../config/columnsConfig';
import { handleStorage, translate } from '../../../utils/Common';

const SelectorColumns = ({ columns, setColumns, isVisible = true, mode = "packages" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [openToLeft, setOpenToLeft] = useState(false);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  const buttonRef = useRef(null);
  const modalRef = useRef(null);
  const dragItem = useRef();
  const dragOverItem = useRef();

  // Función para guardar en localStorage
  const saveToLocalStorage = (newColumns) => {
    handleStorage('set', 'local', `columns-${mode}`, newColumns);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const modalWidth = 260;
      setOpenToLeft(window.innerWidth - rect.right < modalWidth);

      setTimeout(() => {
        modalRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [isOpen]);

  const handleDragStart = (index) => {
    dragItem.current = index;
  };

  const handleDragEnter = (index) => {
    dragOverItem.current = index;
    setDragOverIndex(index);
  };

  const handleDrop = () => {
    const items = [...columns];
    const dragFrom = dragItem.current;
    const dragTo = dragOverItem.current;
    const movedItem = items.splice(dragFrom, 1)[0];
    items.splice(dragTo, 0, movedItem);

    const updated = items.map((col, idx) => ({ ...col, order: idx }));
    setColumns(updated);
    saveToLocalStorage(updated); // GUARDAMOS
    setDragOverIndex(null);
  };

  const toggleColumn = (id) => {
    const updated = columns.map((col) =>
      col.id === id ? { ...col, isVisible: !col.isVisible } : col
    );
    setColumns(updated);
    saveToLocalStorage(updated); // GUARDAMOS
  };

  const resetColumns = () => {
    const raw = rawColumnsByMode[mode]?.map((col, idx) => ({ ...col, order: idx })) || [];
    localStorage.removeItem(`columns-${mode}`);
    setColumns(raw);
  };

  return (
    <div className="selector-container">
      {isVisible && (
        <div ref={buttonRef} style={{ display: 'inline-block' }}>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsOpen(!isOpen)}
            leftIcon={<FaColumns />}
            className="columns-button"
          >
            {translate("global.columns") || "Columns"}
          </Button>
        </div>
      )}

      {isOpen && (
        <div
          ref={modalRef}
          className={`columns-modal ${openToLeft ? 'left-align' : ''}`}
        >
          <div className="modal-header">
            <span className="title">{translate("global.columns") || "Columns"}</span>
            <button className="reset-button" onClick={resetColumns}>
            <RxReset />   {translate("global.reset")}
            </button>
          </div>

          <div className="columns-list">
            {columns
              .slice()
              .sort((a, b) => a.order - b.order)
              .map((col, index) => {
                // Traducir el nombre de la columna dinámicamente usando el mapeo de traducciones
                const getColumnLabel = () => {
                  const columnNameTranslationMap = {
                    'packageId': () => `ID ${translate("global.Package")}`,
                    'tracking': () => translate("global.numTracking"),
                    'packageNum': () => translate("global.numPackage"),
                    'customer': () => translate("global.client"),
                    'pkgStatus': () => translate("global.status"),
                    'location': () => translate("global.ubication"),
                    'doorNum': () => translate("global.numDoor"),
                    'pkgWeight': () => translate("global.weight"),
                    'dateCreatePkg': () => translate("global.dateCreated"),
                    'dateExpiredPkg': () => translate("global.dateExpiration"),
                    'totalCost': () => translate("dashboard.packages.loadpackages.formProvider.costTotal"),
                    'sizePkg': () => translate("global.size"),
                    'clientId': () => `ID ${translate("global.client")}`,
                    'email': () => translate("global.email"),
                    'pkgType': () => translate("global.typeOfPackage"),
                  };
                  const translateFn = columnNameTranslationMap[col.id];
                  return translateFn ? translateFn() : col.name;
                };

                return (
                  <React.Fragment key={col.id}>
                    {dragOverIndex === index && <div className="drop-indicator" />}
                    <div
                      className="column-item"
                      draggable
                      onDragStart={() => handleDragStart(index)}
                      onDragEnter={() => handleDragEnter(index)}
                      onDragEnd={handleDrop}
                    >
                      <MdDragIndicator className="grip-icon" />
                      <label className="column-label">
                        <input
                          type="checkbox"
                          checked={col.isVisible}
                          onChange={() => toggleColumn(col.id)}
                        />
                        {col.icon && <col.icon />} {getColumnLabel()}
                      </label>
                    </div>
                  </React.Fragment>
                );
              })}
            {dragOverIndex === columns.length && <div className="drop-indicator" />}
          </div>
        </div>
      )}
    </div>
  );
};

export default SelectorColumns;
